import { useEffect, useRef, useState } from "react";

const LOGO_INITIAL = "/logo-scroll.png";
const LOGO_SCROLLED = "/logo-initial.png";

const NAV_ITEMS = [
  { href: "/publications", label: "Publications" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onHash = () => setOpen(false);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const handleEnter = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };

  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "backdrop-blur-md bg-black/85 border-b border-white/10"
          : "bg-black border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-12 h-16 md:h-[72px] flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-home"
          className="relative flex items-center group"
          aria-label="Cameron Bergsma — home"
        >
          <span className="relative block w-20 h-20 md:w-24 md:h-24">
            <img
              src={LOGO_INITIAL}
              alt=""
              draggable={false}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${
                scrolled ? "opacity-0" : "opacity-100"
              }`}
            />
            <img
              src={LOGO_SCROLLED}
              alt=""
              draggable={false}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${
                scrolled ? "opacity-100" : "opacity-0"
              }`}
            />
          </span>
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleEnter(item.label)}
                onMouseLeave={handleLeave}
              >
                <button
                  type="button"
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={`group relative px-3 py-2.5 text-[12px] uppercase tracking-[0.18em] font-mono font-semibold transition-colors flex items-center gap-1.5 ${
                    openDropdown === item.label ? "text-white" : "text-white/75 hover:text-white"
                  }`}
                >
                  <span className="relative">
                    {item.label}
                    <span
                      aria-hidden
                      className={`pointer-events-none absolute left-0 -bottom-0.5 h-[2px] bg-[#DC2626] transition-all duration-300 ease-out ${
                        openDropdown === item.label
                          ? "w-full opacity-100"
                          : "w-0 opacity-0 group-hover:w-full group-hover:opacity-100"
                      }`}
                      style={{
                        boxShadow: "0 0 8px rgba(220, 38, 38, 0.7)",
                      }}
                    />
                  </span>
                  <span
                    className={`text-[8px] transition-transform ${
                      openDropdown === item.label ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  >
                    ▾
                  </span>
                </button>
                <div
                  className={`absolute top-full right-0 mt-2 min-w-[200px] rounded-md border border-white/10 bg-black/95 backdrop-blur-md py-2 transition-all duration-200 ${
                    openDropdown === item.label
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 -translate-y-1 pointer-events-none"
                  }`}
                  data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                >
                  {item.children.map((c) => (
                    <a
                      key={c.href}
                      href={c.href}
                      data-testid={`nav-sublink-${c.label.toLowerCase()}`}
                      className="block px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] font-mono font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {c.label}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative px-3 py-2.5 text-[12px] uppercase tracking-[0.18em] font-mono font-semibold text-white/75 hover:text-white transition-colors whitespace-nowrap"
              >
                <span className="relative">
                  {item.label}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-0 -bottom-0.5 h-[2px] w-0 opacity-0 bg-[#DC2626] transition-all duration-300 ease-out group-hover:w-full group-hover:opacity-100"
                    style={{
                      boxShadow: "0 0 8px rgba(220, 38, 38, 0.7)",
                    }}
                  />
                </span>
              </a>
            )
          )}
        </nav>

        {/* Desktop CTA */}
        <a
          href="/contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-[0.18em] text-white relative overflow-hidden cta-clay-blue group"
        >
          <span className="relative z-10">Connect</span>
          <span aria-hidden className="relative z-10 transition-transform group-hover:translate-x-0.5">
            →
          </span>
        </a>

        {/* Mobile / tablet burger (lg-) */}
        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-1.5"
        >
          <span
            className={`block w-6 h-[2px] bg-white transition-transform duration-200 ${
              open ? "translate-y-[7px] rotate-45" : ""
            }`}
          />
          <span
            className={`block w-6 h-[2px] bg-white transition-opacity duration-200 ${
              open ? "opacity-0" : "opacity-100"
            }`}
          />
          <span
            className={`block w-6 h-[2px] bg-white transition-transform duration-200 ${
              open ? "-translate-y-[7px] -rotate-45" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ease-out ${
          open ? "max-h-[640px]" : "max-h-0"
        }`}
        data-testid="mobile-menu"
      >
        <nav className="px-5 pb-6 pt-2 flex flex-col border-t border-white/10">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <div key={item.label} className="border-b border-white/5">
                <button
                  type="button"
                  onClick={() =>
                    setOpenMobileGroup(
                      openMobileGroup === item.label ? null : item.label
                    )
                  }
                  data-testid={`mobile-nav-group-${item.label.toLowerCase()}`}
                  className="w-full py-3 flex items-center justify-between font-display text-2xl tracking-tight text-white/80 hover:text-white"
                >
                  <span>{item.label}</span>
                  <span
                    className={`text-base text-white/40 transition-transform ${
                      openMobileGroup === item.label ? "rotate-180" : ""
                    }`}
                  >
                    ▾
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-[max-height] duration-300 ${
                    openMobileGroup === item.label ? "max-h-48" : "max-h-0"
                  }`}
                >
                  <div className="pl-4 pb-3 flex flex-col">
                    {item.children.map((c) => (
                      <a
                        key={c.href}
                        href={c.href}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-sublink-${c.label.toLowerCase()}`}
                        className="py-2 font-display text-lg tracking-tight text-white/65 hover:text-white"
                      >
                        — {c.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-3 font-display text-2xl tracking-tight text-white/80 hover:text-white border-b border-white/5"
              >
                {item.label}
              </a>
            )
          )}
          <a
            href="/contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-cta"
            className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-xs font-mono uppercase tracking-[0.18em] text-white cta-clay-blue"
          >
            Connect
            <span aria-hidden>→</span>
          </a>
        </nav>
      </div>
    </header>
  );
}
